import React, { HTMLAttributes, useCallback, useEffect, useMemo } from 'react';
import { Control, FieldValues, Path, PathValue, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteRenderOptionState,
  CircularProgress,
  Grid,
  GridSize,
  TextField,
} from '@mui/material';
import { debounce } from 'lodash';

export type LazyAutocompleteInputProps<T, C extends FieldValues = FieldValues> = {
  control?: Control<C>;
  name: Path<C>;
  label: string;
  placeholder?: string;
  options: T[];
  loading?: boolean;
  getOptionLabel: (option: T) => string;
  onSearchChange: (value: string) => void;
  onLoadMore: () => void;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  hasNextPage: boolean | undefined;
  gridSize?: boolean | GridSize | undefined;
  defaultValue?: T | T[] | null;
  disabled?: boolean;
  noOptionsText?: React.ReactNode;
  multiple?: boolean;
  renderOption?: (
    props: HTMLAttributes<HTMLLIElement>,
    option: T,
    state: AutocompleteRenderOptionState
  ) => React.ReactNode;
  value?: T | T[] | null;
  onChange?: (event: React.SyntheticEvent, value: T | T[] | null, reason: AutocompleteChangeReason) => void;
};

export function LazyAutocompleteInput<T, C extends FieldValues = FieldValues>({
  control,
  label,
  options,
  loading,
  name,
  getOptionLabel,
  onSearchChange,
  onLoadMore,
  hasNextPage,
  gridSize = 12,
  disabled = false,
  multiple = false,
  defaultValue,
  isOptionEqualToValue,
  renderOption,
  noOptionsText,
  placeholder = '',
  value: valueProp,
  onChange: onChangeProp,
  ...props
}: LazyAutocompleteInputProps<T, C>) {
  const { t } = useTranslation();

  let field, fieldState;

  if (control) {
    const controller = useController({
      name,
      control,
      defaultValue: defaultValue as PathValue<C, Path<C>>,
    });

    field = controller.field;
    fieldState = controller.fieldState;
  } else {
    field = {
      onChange: onChangeProp,
      value: valueProp ?? null,
      ref: undefined,
      name,
    };

    fieldState = {
      invalid: false,
      error: null,
    };
  }

  const { ref, onChange, value, ...inputProps } = field;
  const { invalid, error } = fieldState;

  const debouncedOnSearchChange = useMemo(() => debounce(onSearchChange, 600), [onSearchChange]);

  const handleSearchInputChange = useCallback(
    (_event: React.SyntheticEvent, value: string) => {
      debouncedOnSearchChange(value);
    },
    [debouncedOnSearchChange]
  );

  const handleOptionSelectChange = (
    event: React.SyntheticEvent,
    newValue: T | T[] | null,
    reason: AutocompleteChangeReason
  ) => {
    onChange?.(newValue);
    onChangeProp?.(event, newValue, reason);
  };

  useEffect(() => {
    return () => {
      debouncedOnSearchChange.cancel();
    };
  }, [debouncedOnSearchChange]);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const reachedBottom = listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1;

    if (reachedBottom && hasNextPage && !loading) {
      onLoadMore();
    }
  };

  return (
    <Grid item xs={gridSize}>
      <Autocomplete
        {...props}
        {...inputProps}
        id={`autocomplete-${name}`}
        filterOptions={(options) => options}
        options={options}
        multiple={multiple}
        getOptionLabel={getOptionLabel}
        noOptionsText={noOptionsText}
        loading={loading}
        onInputChange={handleSearchInputChange}
        onChange={handleOptionSelectChange}
        onOpen={() => {
          if (value) {
            onSearchChange('');
          }
        }}
        disabled={disabled}
        ListboxProps={{ onScroll: handleScroll, style: { overflow: 'auto' } }}
        value={value ?? null}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            error={invalid}
            inputRef={ref}
            helperText={error && t(error.message as string)}
            fullWidth={true}
            InputLabelProps={{ shrink: true }}
            size="small"
            placeholder={placeholder}
          />
        )}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={renderOption}
      />
    </Grid>
  );
}
